<template>
    <!-- form fields for the company section -->

    <section class="flex-column form-fields-company">
        <!-- THE COMPANY : NAME -->
        <el-form-item prop="name" :label="$t.__('common.labels.society_name')">
            <el-input
                v-if="!getLoading"
                ref="name"
                v-model="name"
                name="company"
                autocomplete="on"
                :data-bot="getDatabot('name', currentStore)"
                @input="searchCompanyByName"
                @keyup.enter.native="openPopup()"
            />
            <input-loader
                v-else
                :label="$t.__('thirds.thirdscomponents.FormFieldsCompany.search_in_progress')"
            />
        </el-form-item>
        <div v-if="searchAvailable" class="flex-row mb-2 ai-center">
            <a
                href="javascript:;"
                :data-bot="getDatabot('name-link', currentStore)"
                class="fs-3 ln-20 mr-2 semibold hover-primary-100"
                @click="openPopup()"
            >
                {{ $l10n.__('Rechercher automatiquement les informations de la société') }}
            </a>
        </div>
        <div v-if="companiesList.length > 0" class="mt-0 mb-3">
            <el-alert
                :closable="false"
                class="mb-2 mt-2 alert alert-warning"
                :title="$t.__('common.labels.company_already_exist_in_your_directory')"
                type="warning"
            >
            </el-alert>
            <ul>
                <form-fields-companies-list
                    v-for="(company, index) in companiesList"
                    :key="index"
                    class="form-fields-companies-list br b-1-gray-05 mb-2 gray-0-bkg px-3"
                    style="height: 66px"
                    :company="company"
                    allow-badge
                    :overview="true"
                />
            </ul>
        </div>

        <!-- THE COMPANY : TYPE -->
        <el-form-item prop="type" :label="$t.__('common.labels.type')">
            <el-select v-model="type" class="wp-10" :data-bot="getDatabot('type', currentStore)">
                <el-option
                    v-for="option in typeList"
                    :key="option.label"
                    :value="option"
                    :label="tagsHuman[option].label"
                />
            </el-select>
        </el-form-item>

        <!-- THE COMPANY : REFERENCE -->
        <el-form-item prop="reference" :label="$t.__('common.labels.reference')">
            <el-input
                autocomplete="off"
                v-model="reference"
                :data-bot="getDatabot('reference', currentStore)"
            ></el-input>
        </el-form-item>

        <!-- THE COMPANY : WEBSITE -->
        <el-form-item prop="website" :label="$t.__('common.labels.web_site')">
            <el-input
                ref="website"
                v-model="website"
                name="website"
                autocomplete="off"
                placeholder="https://www.website.com"
                :data-bot="getDatabot('website', currentStore)"
            ></el-input>
        </el-form-item>
        <!-- THE COMPANY : BUSINESS -->
        <el-form-item
            v-if="type !== 'supplier'"
            prop="businessSegments"
            :label="$t.__('common.labels.activity_area')"
        >
            <el-select
                v-model="businessSegments"
                class="wp-10"
                :data-bot="getDatabot('business-segments', currentStore)"
            >
                <el-option
                    v-for="business in businessSegmentsList"
                    :key="business.label"
                    :value="business"
                    :label="business.label"
                />
            </el-select>
        </el-form-item>

        <!-- THE COMPANY : NUMBER OF EMPLOYEES -->
        <el-form-item
            prop="numberOfEmployees"
            :label="$t.__('common.labels.employees_number')"
            class="flex-column"
        >
            <el-select
                v-model="numberOfEmployees"
                class="wp-5"
                :data-bot="getDatabot('number-of-employees', currentStore)"
            >
                <el-option
                    v-for="option in employeesNumberList"
                    :key="option.label"
                    :value="option"
                    :label="option.label"
                />
            </el-select>
        </el-form-item>

        <!-- THE COMPANY : E-MAIL -->
        <el-form-item prop="email" :label="$t.__('common.labels.email')">
            <el-input
                ref="email"
                autocomplete="off"
                v-model="email"
                placeholder="contact@societe.com"
                :data-bot="getDatabot('email', currentStore)"
            ></el-input>
        </el-form-item>

        <!-- THE COMPANY : PHONE / MOBILE / FAX -->
        <contact-number :current-store="currentStore" :error="error" />
    </section>
</template>

<script>
import FormFieldsCompaniesList from 'vcomponents/thirds/thirdscomponents/FormFieldsCompaniesList.vue';
import ContactNumber from 'vcomponents/thirds/thirdscomponents/FormFieldsNumberPhone.vue';
import TagHumansConfig from 'Config/authenticated/TagHumans';
import DataBotsFormsMixins from 'mixins/DataBotsFormsMixins';
import InputLoader from 'vcomponents/utilities/InputLoader.vue';

import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        ContactNumber,
        InputLoader,
        FormFieldsCompaniesList,
    },
    mixins: [DataBotsFormsMixins],
    props: {
        currentStore: {
            type: String,
            required: true,
        },
        error: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },
    },
    data() {
        return {
            companyNameTooltip: this.$l10n.__(
                'Sellsy est capable de vous proposer, via la recherche automatique, des contacts qui peuvent être liés à cette société.\nLes informations trouvées peuvent varier en fonction du contact',
            ),
        };
    },

    computed: {
        searchAvailable() {
            return this.$store.getters[`${this.currentStore}/getSearchAvailable`];
        },
        name: {
            get() {
                return this.$store.getters[`${this.currentStore}/getName`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setName`, value);
                this.$store.dispatch(`${this.currentStore}/generateAccountingCodes`);
            },
        },
        getLoading() {
            return this.$store.getters[`${this.currentStore}/getLoading`];
        },
        typeList() {
            return this.$store.getters[`${this.currentStore}/getTypes`];
        },
        type: {
            get() {
                return this.$store.getters[`${this.currentStore}/getType`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setType`, value);
                this.$emit('change-type', value);
            },
        },
        businessSegmentsList() {
            return this.$store.getters[`${this.currentStore}/getBusinessSegmentsList`];
        },
        businessSegments: {
            get() {
                return this.$store.getters[`${this.currentStore}/getBusinessSegments`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setBusinessSegments`, value);
            },
        },
        employeesNumberList() {
            return this.$store.getters[`${this.currentStore}/getEmployeesNumberList`];
        },
        numberOfEmployees: {
            get() {
                return this.$store.getters[`${this.currentStore}/getNumberOfEmployees`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setNumberOfEmployees`, value);
            },
        },
        email: {
            get() {
                return this.$store.getters[`${this.currentStore}/getEmail`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setEmail`, value);
            },
        },
        reference: {
            get() {
                return this.$store.getters[`${this.currentStore}/getRef`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setRef`, value);
            },
        },
        website: {
            get() {
                return this.$store.getters[`${this.currentStore}/getWebsite`];
            },
            set(value) {
                this.$store.commit(`${this.currentStore}/setWebsite`, value);
            },
        },
        companiesList() {
            const list = this.$store.getters[`${this.currentStore}/getSuggestionCompaniesList`];
            return list && list.length > 0 ? list : [];
        },
        tagsHuman() {
            return TagHumansConfig;
        },
    },
    methods: {
        ...popupPanelMapActions(['open']),
        /**
         * Open a popup throught call of open() from popupPanel.js
         */
        openPopup() {
            this.open({
                key: 'popupThirdForm',
                props: {
                    options: { action: 'search' },
                    current: this.currentStore,
                    defaultValue: this.name,
                },
            });
        },
        searchCompanyByName: _.debounce(function (name) {
            this.$store
                .dispatch(`${this.currentStore}/fetchCompaniesList`, {
                    search: name,
                    siretAllowed: false,
                })
                .then((res) => {
                    this.$store.commit(`${this.currentStore}/setSuggestionCompaniesList`, res);
                });
        }, 400),
    },
};
</script>
